import React, { Component } from 'react';
import googleMaps from 'google-maps';
import L from 'leaflet';
import PropTypes from 'prop-types';
import { each, bind, extend } from 'lodash';
import 'leaflet-draw';
import './utils/google-tile-layer';
import keyMirror from 'keymirror';

// Mixins to kill
import EventHandlerMixin from './mixins/EventHandlerMixin';
import PropertyHelperMixin from './mixins/PropertyHelperMixin';

// Utils
import { logError } from 'api/errors';
import {
  drawDeinit,
  drawInit,
  resetDrawing,
  startCircle,
  startPolygon,
  startRectangle,
  setCircleRadiusUnit
} from './utils/DrawingOperations';

// Constants
import {
  DEFAULT_VIEW_ORIGIN,
  DEFAULT_ZOOM,
  MAX_ZOOM
} from 'constants/map';
import { timeouts } from 'constants/values';
import ShapeType from './PropTypes/ShapeType';

const MAP_STATE = keyMirror({ // TODO: remove keyMirror
  UNINITIALIZED: null,
  HAS_MAP_OBJECT: null
});

/*
 * HOW DRAWSTATE WORKS
 *
 * Caller of MapComponent sets DrawState prop to one of the ShapeType values,
 * that are used to idempontently set desired state of component for
 * drawing shapes.
 *
 * ShapeType.NONE (default) declares that no drawing operation will be
 * happening. MapComponent will set Leaflet state accordingly to reach this
 * state. If a drawing action was already in progress, it will be canceled.
 *
 * ShapeType.SQUARE, ShapeType.CIRCLE, and ShapeType.POLYGON declare
 * that user has ability to draw the specified type of shape. Leaflet
 * state will be set accordingly to reach this state. While in one of these
 * drawing states, onDrawShapeComplete() will be called whenever a shape is
 * created by user via leaflet drawing or edited after the initial drawing.
 * Each time onDrawShapeComplete() is called it passes the current geometry of
 * the created shape. The function passed to onDrawShapeComplete prop can just
 * be an ActionCreator function following the Flux pattern.
 *
 * onDrawShapeComplete() may be called multiple times with user edits. It is
 * up to calling code to decide when the shape is done. Calling code can set
 * drawState to ShapeType.NONE to clear the shape from the map and stop any
 * drawing operations. This will cause any shape on the map to be cleared,
 * and if calling code wants the shape to display after drawing action has
 * finished, then it must add the shape back into a layer.
 *
 * INTERFACE DESIGN
 *
 * There is a lot of Leaflet internal state that is intentionally not exposed
 * to calling code. It's a goal to minimize the exposed interface to essentials.
 *
 * Similarly, MapComponent should not contain logic related to how the shapes
 * will be used. It just accepts requests to enter drawing states and reports
 * back the shapes that were drawn via onDrawShapeComplete handler.
 */

const _wsenToLatLngBounds = (wsen) => new L.LatLngBounds(
  [ [wsen[1], wsen[0]], [wsen[3], wsen[2]] ]);

class MapComponent extends Component {

  constructor(props) {
    super(props);
    this.state = {
      map: null,
      lifecycle: MAP_STATE.UNINITIALIZED
    };

    // Ugly apply of Mixins
    Object.assign(this, EventHandlerMixin);
    Object.assign(this, PropertyHelperMixin);

    this.updatableProps = {
      drawState: function(map) {
        switch (this.props.drawState) {
          case ShapeType.NONE:
            resetDrawing(map);
            break;

          case ShapeType.CIRCLE:
            startCircle(map);
            break;

          case ShapeType.SQUARE:
            startRectangle(map);
            break;

          case ShapeType.POLYGON:
            startPolygon(map);
            break;

          default:
            logError({message:`Unexpected shapetype - ${this.props.drawState}`});
        }
      },
      viewOrigin: function(map) {
        map.setView(this.props.viewOrigin, this.props.zoom, {reset: true, animate: false});
      },
      zoom: function(map) {
        map.setZoom(this.props.zoom);
      },
      maxBounds: function(map) {
        if (this.props.maxBounds) {
          map.setMaxBounds(_wsenToLatLngBounds(this.props.maxBounds));
        }
      },
      fitBounds: function(map) {
        if (this.props.fitBounds) {
          const llb = _wsenToLatLngBounds(this.props.fitBounds);
          setTimeout(function() {
            map.fitBounds(llb, this.props.fitBoundsOptions && this.props.fitBoundsOptions);
          }.bind(this), 100);
        }
      },
      moveDisabled: function(map) {
        if (this.props.moveDisabled) {
          map.dragging.disable();
          map.touchZoom.disable();
          map.doubleClickZoom.disable();
          map.scrollWheelZoom.disable();
          document.getElementsByClassName('leaflet-control-zoom')[0].style.visibility = 'hidden';
        } else {
          map.dragging.enable();
          map.touchZoom.enable();
          map.doubleClickZoom.enable();
          map.scrollWheelZoom.enable();
          document.getElementsByClassName('leaflet-control-zoom')[0].style.visibility = 'inherit';
        }
      },
      zoomControlPosition: function(map) {
        var zoomControl = map.zoomControl;
        if (zoomControl) {
          zoomControl.setPosition(this.props.zoomControlPosition);
        }
      }
    };
  }

  componentDidMount() {
    window.L = L;
    const mapOptions = {
      keyboard: false,
      attributionControl: false,
      bounceAtZoomLimits: this.props.bounceAtZoomLimits,
      zoomControl: this.props.showZoomControl,
      drawControl: true
    };

    const map = window.L.map(this.mapDiv, mapOptions).setView(this.props.viewOrigin, this.props.zoom);

    // Pane meant for holding layers that are intended to be interactive. Its z-index is set so that other layers that
    // might interfere with mouse actions will be behind it.
    map.createPane('interactive');

    this.registerEventHandlers(map, this.props);

    // Google Maps setup.
    if (this.props.googleConfig && this.props.googleConfig.key) {
      googleMaps.KEY = this.props.googleConfig.key;
    } else {
      // Do not ignore this error, even if maps seem to work anyhow.
      logError({message:'Expected Google Maps credentials have not been provided!'});
    }

    // Set the Google Maps API version we want to load.
    // If you see "Google Maps JavaScript API warning: RetiredVersion" in the console,
    // then update this version.
    // See https://developers.google.com/maps/documentation/javascript/versions
    googleMaps.VERSION = '3.35';

    // Add google maps layer by default
    googleMaps.load(function() {

      // Make sure this component wasn't unloaded during load
      if (this.mapDiv) {
        const googleLayer = new L.Google('HYBRID');
        map.addLayer(googleLayer);

        // HACK: Leaflet Plugin for google maps doesn't pass the
        // clickableIcons property through when creating the map, so we need
        // to wait a bit for the map to be added, and see if we can get
        // a handle to the googleMap and set it manually.  This is to disable
        // the pop up that shows when click points of interest (parks, etc)
        setTimeout(() => {
          const googleMap = googleLayer._google;
          if (googleMap && googleMap.setClickableIcons) {
            googleMap.setClickableIcons(false);
          }
        }, timeouts.HIDE_GOOGLE_MAP_ICONS);

        if (this.props.showScale) {
          L.control.scale().addTo(map);
        }
      }
    }.bind(this));

    setCircleRadiusUnit(map, this.props.unitsOfMeasure);
    drawInit({map, onDrawShapeComplete: this.props.onDrawShapeComplete});

    // Re disablement below: my analysis is that worst case the setState() call causes one extra call to render().
    // And I'm reluctant to mess with working code here. https://github.com/airbnb/javascript/issues/684
    this.setState({ // eslint-disable-line react/no-did-mount-set-state
      map: map,
      lifecycle: MAP_STATE.HAS_MAP_OBJECT
    });

    // if any updatable properties were sent on initial map load, handle these now that we have a map.
    this.updateProps({}, map);
  }

  componentDidUpdate(oldProps) {
    this.updateProps(oldProps, this.state.map);
  }

  componentWillUnmount() {
    drawDeinit(this.state.map);
    this.unRegisterEventHandlers(this.state.map);
    this.state.map.remove();
    this.state.map = null;
  }

  // Update each of this map's properties that have changed from the given old
  // properties method
  updateProps(oldProps, map) {
    const that = this;
    each(this.propUpdateMethods(oldProps), function(updateMethod) {
      bind(updateMethod, that, map)();
    });
  }

  render() {
    const style = {
      width: '100%',
      height: '100%'
    };

    let children = [];
    if (this.state.lifecycle === MAP_STATE.HAS_MAP_OBJECT) {
      const childProps = {
        map: this.state.map
      };
      children = React.Children.map(this.props.children, function(child, index) {
        return React.isValidElement(child)
          ? React.cloneElement(child, extend({key: index}, childProps)) : child;
      });
    }

    return (
      <div className={this.props.className} onClick={this.props.onClick}>
        <div id='leaflet-controlled-div' ref={ (mapDiv) => { this.mapDiv = mapDiv; } } style={style} />
        {children}
      </div>
    );
  }
}

MapComponent.propTypes = {
  bounceAtZoomLimits: PropTypes.bool, // http://leafletjs.com/reference-1.0.3.html#map-bounceatzoomlimits
  children: PropTypes.node,
  className: PropTypes.string, // Set CSS class of rendered MapComponent.
  drawState: PropTypes.oneOf(Object.values(ShapeType)), // Intended drawing state. See "How DrawState works" above
  fitBounds: PropTypes.arrayOf(PropTypes.number), // http://leafletjs.com/reference-1.0.3.html#map-fitbounds but in [W,S,E,N] format used by Turf.
  fitBoundsOptions: PropTypes.object, // http://leafletjs.com/reference-1.0.3.html#fitbounds-options
  googleConfig: PropTypes.shape({ key: PropTypes.string }), // Credentials and any other info needed for use of Google API.
  maxBounds: PropTypes.arrayOf(PropTypes.number), // http://leafletjs.com/reference-1.0.3.html#map-maxbounds but in [W,S,E,N] format used by Turf.
  maxZoom: PropTypes.number, // http://leafletjs.com/reference-1.0.3.html#map-maxzoom
  moveDisabled: PropTypes.bool, // True to disable map movement from user interactions.
  onClick: PropTypes.func, // Function to call when user clicks on map area, but not on a map layer that handles a click.
  onDrawShapeComplete: PropTypes.func, // Callback to receive completed shapes from user drawing. See "How DrawState works" above.
  showScale: PropTypes.bool, // True to show the scale control over map ( http://leafletjs.com/reference-1.0.3.html#control-scale )
  showZoomControl: PropTypes.bool, // True to show the zoom control over map ( http://leafletjs.com/reference-1.0.3.html#control-zoom )
  unitsOfMeasure: PropTypes.string, // User measure selected template
  viewOrigin: PropTypes.arrayOf(PropTypes.number), // Set coordinates on which map will center. 1st element is lat (Y) and 2nd is long (X).
  zoom: PropTypes.number, // http://leafletjs.com/reference-1.0.3.html#map-zoom
  zoomControlPosition: PropTypes.string // http://leafletjs.com/reference-1.0.3.html#control-zoom-position
};

MapComponent.defaultProps = {
  bounceAtZoomLimits: true,
  drawState: ShapeType.NONE,
  maxZoom: MAX_ZOOM,
  showZoomControl: true,
  viewOrigin: DEFAULT_VIEW_ORIGIN,
  zoom: DEFAULT_ZOOM
};

export default MapComponent;
